import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StoreMenu from "../StoreMenu";
import { getStoreInvoicePrinter, getStoreLocalData, getUserData } from "../../utils/localStorage";
import {
  formatDate,
  formatDateWithTime,
} from "../../constants/DateUtil/DateUtil";

import qz from "qz-tray";
import ButtonLink from "../../ButtonLink";
import AdminMenu from "../AdminMenu";

const DashboardPaymentCollectionReceipt = () => {
  const { storeData, brandProfileData, userProfileData ,userProfileRole} = getUserData();

  let store = storeData;
  let brand = brandProfileData;
  const userProfile = userProfileData;

  const { storeLocalData } = getStoreLocalData();

  const userRole = userProfileRole;

  const location = useLocation();
  const { paymentCollectionData, orderFilterData } = location.state || {}; // Fallback to prevent undefined errors

  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const startDate = new Date(
    Math.min(...paymentCollectionData.map((item) => new Date(item.createdDate)))
  ).toISOString();

  const endDate = new Date(
    Math.max(...paymentCollectionData.map((item) => new Date(item.createdDate)))
  ).toISOString();

  const data = {
    storeName: store.storeName,
    brandName: brand.name,
    receivedBy: userProfile.firstName + " " + userProfile.lastName,
  };

  const totalCollectionData = paymentCollectionData.reduce(
    (acc, item) => acc + item.payment.amount,
    0
  );

  const [isConnected, setIsConnected] = useState(false);

  const connectToQZ = async () => {
    try {
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect();
        setIsConnected(true);
      }
    } catch (err) {
      console.error("Connection error:", err);
    }
  };

  const [invoicePrinterValue, setInvoicePrinterValue] = useState("");
  const fetchSelectedInvoicePrinter = async () => {
    try {
      const selectedInvoicePrinterData = await getStoreInvoicePrinter();
      setInvoicePrinterValue(selectedInvoicePrinterData?.invoicePrinter || "");
    } catch {
      setInvoicePrinterValue("");
    }
  };

  useEffect(() => {
    if (!isConnected) {
      connectToQZ();
    }

    fetchSelectedInvoicePrinter();
  }, []);

  const printInvoiceHTML = async () => {
    try {
      const printer = await qz.printers.find(invoicePrinterValue);
      const config = qz.configs.create(printer, {
        encoding: "UTF-8",
        size: { width: 3 },
        units: "in",
        density: "600",
        colorType: "grayscale",
      });
  
      const paymentCollectionHtml = paymentCollectionData
        .map((item) => {
          const billNo = item.order.invoiceNo;
          const mode = item.payment.paymentMode.replace(/_/g, " ");
          const user = item.payment.receiver.firstName;
          const paymentAmount = item.payment.amount;
  
          return `
            <div style="display: flex;">
              <p style="flex: 1; font-size: 12px; margin: 0; text-align: left;">${billNo}</p>
              <p style="flex: 1; font-size: 12px; margin: 0; text-align: left;">${mode}</p>
              <p style="flex: 1; font-size: 12px; margin: 0; text-align: right;">${user}</p>
              <p style="flex: 1; font-size: 12px; margin: 0; text-align: right;">${storeLocalData?.currencySymbol} ${paymentAmount}</p>
            </div>
          `;
        })
        .join("");
  
      const paymentSummaryHtml = Object.entries(
        paymentCollectionData.reduce((acc, item) => {
          const mode = item.payment.paymentMode;
          acc[mode] = (acc[mode] || 0) + item.payment.amount;
          return acc;
        }, {})
      )
        .map(([mode, total]) => `
          <div style="display: flex; justify-content: space-between;">
            <span><strong>${mode.replace(/_/g, " ")}</strong></span>
            <span>${storeLocalData?.currencySymbol} ${total}</span>
          </div>
        `)
        .join("");
  
      const userSummaryHtml = Object.entries(
        paymentCollectionData.reduce((acc, item) => {
          const receiver = item.payment.receiver.firstName;
          acc[receiver] = (acc[receiver] || 0) + item.payment.amount;
          return acc;
        }, {})
      )
        .map(([receiver, total]) => `
          <div style="display: flex; justify-content: space-between;">
            <span><strong>${receiver}</strong></span>
            <span>${storeLocalData?.currencySymbol} ${total}</span>
          </div>
        `)
        .join("");
  
      const htmlContent = `
        <html>
          <body style="font-size: 12px; font-family: sans-serif;">
            <div style="text-align: center;">
              <span style="font-size: 18px; font-weight: bold;">Payment Collection</span>
              <hr style="border-top: 1px dashed #000;"/>
              ${data.storeName ? `<p style="font-size: 14px; font-weight: bold; margin: 0;">${data.storeName}</p>` : ""}
              <p style="font-size: 12px; margin: 0;">Period - ${formatDate(startDate)} - ${formatDate(endDate)}</p>
              <hr style="border-top: 1px dashed #000;"/>
              <div style="display: flex;">
                <p style="flex: 1; font-weight: bold; margin: 0; text-align: left;">Bill No.</p>
                <p style="flex: 1; font-weight: bold; margin: 0; text-align: left;">Mode</p>
                <p style="flex: 1; font-weight: bold; margin: 0; text-align: right;">User</p>
                <p style="flex: 1; font-weight: bold; margin: 0; text-align: right;">Bal</p>
              </div>
              ${paymentCollectionHtml}
              <hr style="border-top: 1px dashed #000;"/>
              <div style="display: flex; justify-content: space-between;">
                <span><strong>Total:</strong></span>
                <span>${storeLocalData?.currencySymbol} ${totalCollectionData}</span>
              </div>
              <hr style="border-top: 1px dashed #000;"/>
              <p style="font-size: 14px; font-weight: bold; margin: 0;">Payment Summary</p>
              ${paymentSummaryHtml}
              <hr style="border-top: 1px dashed #000;"/>
              <p style="font-size: 14px; font-weight: bold; margin: 0;">User Summary</p>
              ${userSummaryHtml}
              <hr style="border-top: 1px dashed #000;"/>
              <p style="font-size: 12px; margin: 0;">${formatDateWithTime(new Date())}</p>
            </div>
          </body>
        </html>
      `;
  
      await qz.print(config, [{ type: "html", format: "plain", data: htmlContent }]);
      // alert("HTML challan sent to printer!");
    } catch (err) {
      console.error("Print failed:", err);
      alert("Failed to print challan");
    }
  };
  

  return (
    <div className="flex-1">
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{
          "--surface-0": "#1752AA",
        }}
      >
        <div className="flex-1 flex ml-3 mr-3">
          <div className="flex-1 flex ">
            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                <i
                  className="pi pi-angle-left"
                  onClick={() =>
                    navigate("/DashboardPaymentCollection", {
                      state: {
                        orderFilterData: orderFilterData,
                        // customerData: customerData,
                        // storeCustomerId: storeCustomerId,
                      },
                    })
                  }
                  style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
                ></i>
              </div>
            </div>
            <div className="flex-1 ml-5">
              <p className="text-white text-2xl font-bold uppercase">
                Payment Collection
              </p>
            </div>
            <div className="flex">
              <div className="mx-4 cursor-pointer" onClick={printInvoiceHTML}>
                <img
                  src={"images/printer.png"}
                  style={{
                    width: 35,
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() => setVisible(true)}
          >
            <i
              className="pi pi-bars"
              style={{ fontSize: "27px", marginRight: 30, color: "#fff" }}
            ></i>
          </div>
        </div>
      </div>

      <div className="flex justify-content-between mt-2">
        <div></div>
        <div className="flex justify-content-center flex-column print-section">
          <p className="text-3xl font-medium text-center">Payment Collection</p>
          <div className="flex justify-content-center align-items-center">
            <p className="w-7  border-dashed border-1 "></p>
          </div>
          <p className="text-xl font-medium text-center">{data?.storeName}</p>
          <p className="text-xl font-medium text-center">
            Period - {formatDate(startDate)} - {formatDate(endDate)}
          </p>
          <div className="flex justify-content-center align-items-center">
            <p className="w-5  border-dashed border-1 "></p>
          </div>

          <div className="flex">
            <p
              className="text-lg font-medium text-left mx-2"
              style={{ flex: 1 }}
            >
              BIll
            </p>
            <p
              className="text-lg font-medium text-left mx-2"
              style={{ flex: 1 }}
            >
              Mode
            </p>
            <p
              className="text-lg font-medium text-left ml-4"
              style={{ flex: 1.5 }}
            >
              User
            </p>
            <p
              className="text-lg font-medium text-right mr-2"
              style={{ flex: 1 }}
            >
              Amt
            </p>
          </div>
          {paymentCollectionData.map((item) => (
            <div className="flex " key={item.order.invoiceNo}>
              <p
                className="text-lg font-normal text-left mx-2"
                style={{ flex: 1 }}
              >
                {item.order.invoiceNo}
              </p>
              <p
                className="text-lg font-normal text-left  mx-2"
                style={{ flex: 1 }}
              >
                {item.payment.paymentMode.replace(/_/g, " ")}
              </p>
              <p
                className="text-lg font-normal text-right  mx-2"
                style={{ flex: 2.5 }}
              >
                {item.payment.receiver.firstName}
              </p>
              <p
                className="text-lg font-normal text-right  mx-2"
                style={{ flex: 1.5 }}
              >
                {storeLocalData?.currencySymbol} {item.payment.amount}
              </p>
            </div>
          ))}

          <div className="flex ">
            <p className="text-lg font-medium text-left" style={{ flex: 2 }}>
              Total :
            </p>
            <p
              className="text-lg font-medium text-left"
              style={{ flex: 2 }}
            ></p>
            <p className="text-lg font-medium text-right" style={{ flex: 1 }}>
              {storeLocalData?.currencySymbol} {totalCollectionData}
            </p>
          </div>
          <div className="flex justify-content-center align-items-center">
            <p className="w-5  border-dashed border-1 "></p>
          </div>
          <div className="flex">
            <p className="text-lg font-medium text-center" style={{ flex: 2 }}>
              Payment Summary
            </p>
          </div>
          {Object.entries(
            paymentCollectionData.reduce((acc, item) => {
              const mode = item.payment.paymentMode;
              acc[mode] = (acc[mode] || 0) + item.payment.amount;
              return acc;
            }, {})
          ).map(([mode, total]) => (
            <div className="flex" key={mode}>
              <p className="text-lg font-medium text-left" style={{ flex: 2 }}>
                {mode.replace(/_/g, " ")}
              </p>
              <p
                className="text-lg font-medium text-left"
                style={{ flex: 2 }}
              ></p>
              <p className="text-lg font-medium text-right" style={{ flex: 1 }}>
                {storeLocalData?.currencySymbol} {total}
              </p>
            </div>
          ))}
          <div className="flex justify-content-center align-items-center">
            <p className="w-5  border-dashed border-1 "></p>
          </div>
          <div className="flex">
            <p className="text-lg font-medium text-center" style={{ flex: 2 }}>
              User Summary
            </p>
          </div>
          {Object.entries(
            paymentCollectionData.reduce((acc, item) => {
              const receiver = item.payment.receiver.firstName;
              acc[receiver] = (acc[receiver] || 0) + item.payment.amount;
              return acc;
            }, {})
          ).map(([receiver, total]) => (
            <div className="flex" key={receiver}>
              <p className="text-lg font-medium text-left" style={{ flex: 2 }}>
                {receiver}
              </p>
              <p
                className="text-lg font-medium text-left"
                style={{ flex: 2 }}
              ></p>
              <p className="text-lg font-medium text-right" style={{ flex: 1 }}>
                {storeLocalData?.currencySymbol} {total}
              </p>
            </div>
          ))}

          <p className="text-xl font-medium text-center">
            {formatDateWithTime(new Date())}
          </p>
          <div className="flex justify-content-center align-items-center">
            <p className="w-7  border-dashed border-1 "></p>
          </div>
        </div>

        <div className="ml-4 cursor-pointer" onClick={() => window.print()}>
          <img
            src={"Icons/printer.png"}
            style={{
              width: 60,
            }}
          />
        </div>
      </div>
      {/* Menu component */}
      {userRole === "BY_ADMIN" || userRole === "BY_SUB_ADMIN_USER" ? (
        <AdminMenu setVisible={setVisible} visible={visible} />
      ) : (
        <>
        <StoreMenu setVisible={setVisible} visible={visible} />
      <ButtonLink />
      </>
      )}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-section, .print-section * {
              visibility: visible;
            }
            .print-section {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              page-break-after: always;
            }
            @page {
              size: 80mm auto;
              margin: 2mm;
            }
          }
        `}
      </style>
    </div>
  );
};

export default DashboardPaymentCollectionReceipt;
